/**
 * Higgsfield風 ホームページカテゴリセクション自動再生
 * セクションがビューポートに入ったら動画を自動再生、離れたら停止
 *
 * 最適化戦略（Higgsfield.ai方式）:
 * - 最初の2個を即座に再生（初回ロード軽量化）
 * - 残りの動画はIntersection Observerで段階的にロード・再生
 */

class HomepageVideoAutoplay {
  constructor() {
    this.sections = [];
    this.activeVideos = new Map(); // セクションごとのアクティブ動画を管理
    this.videoObservers = new Map(); // 個別動画のObserver管理
    this.firstLoadCount = 2; // 最初に即座に再生する動画数（Higgsfield.ai方式）
    this.userInteracted = false; // ユーザー操作後のみ再生
    this.pendingSections = new Set();
    this.init();
  }

  init() {
    // すべてのカテゴリセクションを取得
    this.sections = document.querySelectorAll('.category-section');

    if (this.sections.length === 0) {
      return;
    }

    // セクション単位のIntersection Observerを設定
    const sectionOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25 // セクションが25%表示されたら発火
    };

    const sectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          if (this.userInteracted) {
            this.playSection(entry.target);
          } else {
            this.pendingSections.add(entry.target);
          }
        } else {
          this.pendingSections.delete(entry.target);
          this.pauseSection(entry.target);
        }
      });
    }, sectionOptions);

    // 各セクションを監視
    this.sections.forEach(section => {
      sectionObserver.observe(section);
    });

    const activate = () => {
      if (this.userInteracted) return;
      this.userInteracted = true;
      this.pendingSections.forEach(section => this.playSection(section));
      this.pendingSections.clear();
      ['pointerdown', 'touchstart', 'keydown'].forEach(evt => {
        window.removeEventListener(evt, activate);
      });
    };
    ['pointerdown', 'touchstart', 'keydown'].forEach(evt => {
      window.addEventListener(evt, activate, { passive: true });
    });
  }

  playSection(section) {
    // すでに再生中の場合はスキップ
    if (this.activeVideos.has(section)) {
      return;
    }

    const videoCards = section.querySelectorAll('.category-video-card');
    const allVideos = [];

    // すべての動画要素を収集
    videoCards.forEach(card => {
      const video = card.querySelector('.category-video');
      if (video) {
        allVideos.push({ card, video });
      }
    });

    // Higgsfield.ai方式: 最初のN個だけ即座に再生
    const immediateVideos = allVideos.slice(0, this.firstLoadCount);
    const lazyVideos = allVideos.slice(this.firstLoadCount);

    // 最初の2個を即座に再生
    immediateVideos.forEach(({ video }) => {
      this.loadAndPlayVideo(video);
    });

    // 残りの動画は個別にIntersection Observerで監視
    if (lazyVideos.length > 0) {
      const videoObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const card = entry.target;
            const video = card.querySelector('.category-video');
            if (video) {
              this.loadAndPlayVideo(video);
              videoObserver.unobserve(card); // 一度再生したら監視解除
            }
          }
        });
      }, {
        root: null,
        rootMargin: '50px', // 少し手前から準備
        threshold: 0.1
      });

      // 残りの動画カードを監視
      lazyVideos.forEach(({ card }) => {
        videoObserver.observe(card);
      });

      // Observer参照を保存（セクション離脱時にクリーンアップ）
      this.videoObservers.set(section, videoObserver);
    }

    // アクティブ動画を記録
    this.activeVideos.set(section, allVideos.map(v => v.video));

    const sectionTitle = section.querySelector('.category-title')?.textContent || 'Unknown';
  }

  loadAndPlayVideo(video) {
    // 遅延ロード: data-srcがある場合は設定
    if (!video.src && video.dataset.src) {
      video.src = video.dataset.src;
      video.preload = 'metadata';
      video.load();
    }

    // ミュートして自動再生（ブラウザポリシー対応）
    video.muted = true;
    video.loop = true;

    // 動画を表示（CSSではホバー時のみ表示される設定のため）
    video.style.display = 'block';

    // 再生を試みる
    video.play().catch(error => {
      // 自動再生がブロックされた場合（正常）
      if (error.name !== 'AbortError') {
        const section = video.closest('.category-section');
        if (section) {
          this.pendingSections.add(section);
        }
      }
    });
  }

  pauseSection(section) {
    const videosToStop = this.activeVideos.get(section);

    if (!videosToStop) {
      return;
    }

    // すべての動画を停止
    videosToStop.forEach(video => {
      if (!video.paused) {
        video.pause();
      }
      // 動画を非表示に戻す（サムネイルを表示）
      video.style.display = 'none';
    });

    // Observerをクリーンアップ（メモリリーク防止）
    const videoObserver = this.videoObservers.get(section);
    if (videoObserver) {
      videoObserver.disconnect();
      this.videoObservers.delete(section);
    }

    // アクティブ動画リストから削除
    this.activeVideos.delete(section);
  }
}

// DOMContentLoaded後に初期化
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new HomepageVideoAutoplay();
  });
} else {
  new HomepageVideoAutoplay();
}
